import { useCallback, useMemo } from 'react'

import type { ColumnParameters } from '../../contexts/ColumnParametersContext.js'
import { ariaOffset } from '../../helpers/constants.js'
import type { OrderBy } from '../../helpers/sort.js'
import { toggleColumn, toggleColumnExclusive } from '../../helpers/sort.js'
import ColumnHeader from '../ColumnHeader/ColumnHeader.js'

interface TableHeaderProps {
  columnsParameters: ColumnParameters[]
  ariaRowIndex: number // aria row index for the header
  orderBy?: OrderBy // array of column order by clauses. If undefined, the table is unordered, the sort elements are hidden and the interactions are disabled.
  onOrderByChange?: (orderBy: OrderBy) => void // callback to call when a user interaction changes the order. The interactions are disabled if undefined.
  canMeasureColumn?: Record<string, boolean> // indicates if the width of a column can be measured.
  exclusiveSort?: boolean // whether to use exclusive sort mode
}

/**
 * Render a header for a table.
 */
export default function TableHeader({
  columnsParameters, orderBy, onOrderByChange, canMeasureColumn, ariaRowIndex, exclusiveSort,
}: TableHeaderProps) {
  // Function to handle click for changing orderBy
  const getToggleOrderBy = useCallback((columnHeader: string) => {
    if (!onOrderByChange || !orderBy) return undefined
    return () => {
      const next = exclusiveSort ? toggleColumnExclusive(columnHeader, orderBy) : toggleColumn(columnHeader, orderBy)
      onOrderByChange(next)
    }
  }, [orderBy, onOrderByChange, exclusiveSort]
  )

  const orderByColumn = useMemo(() => {
    return new Map((orderBy ?? []).map(({ column, direction }, index) => [column, { direction, index }]))
  }, [orderBy])

  return columnsParameters.map((columnParameters, visibleColumnIndex) => {
    const { name, index: columnIndex, className, ...columnConfig } = columnParameters
    // Note: columnIndex is the index of the column in the dataframe header
    // and not the index of the column in the table (which can be different if
    // some columns are hidden, or if the order is changed)
    return (
      // The ColumnHeader component width is controlled by the parent
      <ColumnHeader
        key={columnIndex}
        canMeasureWidth={canMeasureColumn?.[name] === true}
        direction={orderByColumn.get(name)?.direction}
        orderByIndex={orderByColumn.get(name)?.index}
        orderBySize={orderBy?.length}
        toggleOrderBy={getToggleOrderBy(name)}
        columnName={name}
        columnIndex={columnIndex}
        className={className}
        ariaColIndex={visibleColumnIndex + ariaOffset}
        ariaRowIndex={ariaRowIndex}
        columnConfig={columnConfig}
      >
        {name}
      </ColumnHeader>
    )
  })
}
